import { PayloadAction } from '@reduxjs/toolkit';

import {
  FitBoundsPayload,
  FlyTo,
  HcMapPreferences,
  HcMapSliceState,
  HcMapState,
  HeatmapColorTheme,
  MapStyles,
  MlsCoveragePayload,
  MoveMapProps,
  PropertyTypeEnum,
  VectilesMetricGroups,
  VectilesMetricIds,
} from '@hcs/types';
import { createHcReduxSlice } from '@hcs/utils';

import {
  fitGeoLocationsToViewPort,
  getBoundsInfoFromViewportState,
} from '../utils/viewport.utils';

export const INITIAL_ZOOM = 14;
const PADDING = 50;

export type HcMapMountArgs = {
  mapId: string;
  mapState: Omit<HcMapState, 'boundsInfo' | 'isMapMoving' | 'heatmapTheme'> & {
    boundsInfo?: HcMapState['boundsInfo'];
    heatmapTheme?: HcMapState['heatmapTheme'];
  };
};
export type HcMapShowMonochromeArgs = {
  mapId: string;
  visible: boolean;
};
export type HcMapChangeVectileGroupArgs =
  | {
      mapId: string;
      vectilesMetricGroup: null;
      vectielsMetricId?: null;
    }
  | {
      mapId: string;
      vectilesMetricGroup: VectilesMetricGroups;
      vectilesMetricId: VectilesMetricIds;
    };
export type HcMapChangeVectileMetricArgs = {
  mapId: string;
  vectilesMetricId: VectilesMetricIds | null;
};
export type HcMapDisableMarkerClustersArgs = {
  mapId: string;
  disableClusters: boolean;
};
export const initialState: HcMapSliceState = {};
export const hcMapSlice = createHcReduxSlice({
  name: 'map',
  initialState,
  reducers: {
    hcMapMount(state, action: PayloadAction<HcMapMountArgs>) {
      state[action.payload.mapId] = {
        ...action.payload.mapState,
        boundsInfo:
          action.payload.mapState.boundsInfo ||
          getBoundsInfoFromViewportState(action.payload.mapState.viewport),
        heatmapTheme:
          action.payload.mapState.heatmapTheme || HeatmapColorTheme.Solutions,
        isMapMoving: false,
      };
    },
    hcMapViewportChange(
      state,
      action: PayloadAction<{
        mapId: string;
        viewport?: MoveMapProps;
        options?: {
          changeZoom?: number;
        };
      }>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        if (action.payload.options?.changeZoom) {
          map.viewport.zoom += action.payload.options?.changeZoom;
        }
        map.viewport = { ...map.viewport, ...action.payload.viewport };
        map.boundsInfo = getBoundsInfoFromViewportState(map.viewport);
      }
    },
    hcMapMlsCoverageChange(
      state,
      action: PayloadAction<{
        mapId: string;
        mlsCoverage: Omit<MlsCoveragePayload, 'mapId'>;
      }>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.mlsCoverage = {
          ...map.mlsCoverage,
          ...action.payload.mlsCoverage,
        };
      }
    },
    hcMapShowMarkers(
      state,
      action: PayloadAction<{
        mapId: string;
        showMarkers: boolean;
      }>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.markers.showMarkers = action.payload.showMarkers;
      }
    },
    hcMapDisableMarkerClusters(
      state,
      action: PayloadAction<HcMapDisableMarkerClustersArgs>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.markers.disableClusters = action.payload.disableClusters;
      }
    },
    hcMapLayersShowMonochrome(
      state,
      action: PayloadAction<HcMapShowMonochromeArgs>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.heatmap.showMonochrome = action.payload.visible;
      }
    },
    hcMapChangeVectilesMetricGroup(
      state,
      action: PayloadAction<HcMapChangeVectileGroupArgs>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.heatmap.vectilesMetricGroup = action.payload.vectilesMetricGroup;
        if (action.payload.vectilesMetricGroup) {
          map.heatmap.vectilesMetricId = action.payload.vectilesMetricId;
        } else {
          map.heatmap.vectilesMetricId = null;
        }
      }
    },
    hcMapLayersChangePropertyType(
      state,
      action: PayloadAction<{
        mapId: string;
        propertyType?: PropertyTypeEnum | null;
      }>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.heatmap.propertyType = action.payload.propertyType;
      }
    },
    hcMapChangeZoom(
      state,
      action: PayloadAction<{
        mapId: string;
        zoom: number;
      }>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.viewport = {
          ...map.viewport,
          zoom: action.payload.zoom,
        };
        map.boundsInfo = getBoundsInfoFromViewportState(map.viewport);
      }
    },
    hcMapChangeMapStyle(
      state,
      action: PayloadAction<{
        mapId: string;
        mapStyle: MapStyles;
      }>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.mapStyle = action.payload.mapStyle;
      }
    },
    hcMapChangeVectilesMetric(
      state,
      action: PayloadAction<HcMapChangeVectileMetricArgs>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.heatmap.vectilesMetricId = action.payload.vectilesMetricId;
      }
    },
    hcMapApplyPreferences(
      state,
      action: PayloadAction<{
        mapId: string;
        preferences: HcMapPreferences;
      }>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.heatmap = {
          ...map.heatmap,
          ...action.payload.preferences.heatmap,
        };
      }
    },
    hcMapSetHeatmapTheme(
      state,
      action: PayloadAction<{
        mapId: string;
        heatmapTheme: HeatmapColorTheme;
      }>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.heatmapTheme = action.payload.heatmapTheme;
      }
    },
    hcMapFitBoundsToGeoLocations(
      state,
      action: PayloadAction<FitBoundsPayload>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        const viewPortInfo = fitGeoLocationsToViewPort(
          action.payload.coords,
          map,
          action.payload.padding || PADDING,
        );

        if (viewPortInfo) {
          const { coords: nextCoords, viewport: nextViewport } = viewPortInfo;
          map.viewport = {
            latitude: nextCoords.lat,
            longitude: nextCoords.lng,
            height: nextViewport.height,
            width: nextViewport.width,
            zoom: nextViewport.zoom,
          };
          map.boundsInfo = getBoundsInfoFromViewportState(map.viewport);
          map.fitId = action.payload.fitId;
        }
      }
    },
    hcMapFlyTo(
      state,
      action: PayloadAction<{
        mapId: string;
        flyTo?: FlyTo;
      }>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.flyTo = action.payload.flyTo;
      }
    },
    hcMapSetIsMoving(
      state,
      action: PayloadAction<{ mapId: string; isMapMoving: boolean }>,
    ) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.isMapMoving = action.payload.isMapMoving;
      }
    },
    hcMapDebugToggle(state, action: PayloadAction<{ mapId: string }>) {
      const map = state?.[action.payload.mapId];
      if (map) {
        map.debug = !map.debug;
      }
    },
  },
});
